/// Audio modes from the proctoring room metadata (written by the API):
/// `{ audioMode, audioGroups: { "staff-<id>": { mode, members: ["cand-<id>", ...] } } }`.
/// A candidate follows its proctor's group mode, otherwise the room mode; without any setting the mode is "auto".
/// Legacy `openAudio`/`openAudioGroups` keys (before audio modes) are read as "open".
export type AudioMode = "auto" | "open" | "listen" | "muted";
export type EffectiveAudio = "open" | "listen" | "muted";

export interface AudioState {
  roomMode: AudioMode;
  groups: Record<string, { mode: AudioMode; members: string[] }>;
}

export interface CandidateAudioSetting {
  mode: AudioMode;
  /** "room" or the proctor's staff identity: candidates hear each other only within the same scope. */
  scope: string;
}

export const AUDIO_MODES: AudioMode[] = ["auto", "open", "listen", "muted"];
export const DEFAULT_AUDIO: AudioState = { roomMode: "auto", groups: {} };

const isMode = (value: unknown): value is AudioMode => typeof value === "string" && (AUDIO_MODES as string[]).includes(value);
const strings = (value: unknown) => (Array.isArray(value) ? value.filter((m): m is string => typeof m === "string") : []);

export const readAudioState = (metadata?: string): AudioState => {
  if (!metadata) return DEFAULT_AUDIO;
  try {
    const parsed = JSON.parse(metadata);
    const roomMode: AudioMode = isMode(parsed?.audioMode) ? parsed.audioMode : parsed?.openAudio === true ? "open" : "auto";
    const groups: AudioState["groups"] = {};
    const modern = parsed?.audioGroups;
    if (modern && typeof modern === "object") {
      Object.entries(modern).forEach(([staff, g]: [string, any]) => {
        if (isMode(g?.mode)) groups[staff] = { mode: g.mode, members: strings(g.members) };
      });
    } else if (parsed?.openAudioGroups && typeof parsed.openAudioGroups === "object") {
      Object.entries(parsed.openAudioGroups).forEach(([staff, members]) => (groups[staff] = { mode: "open", members: strings(members) }));
    }
    return { roomMode, groups };
  } catch {
    return DEFAULT_AUDIO;
  }
};

export const candidateSetting = (state: AudioState, identity: string): CandidateAudioSetting => {
  for (const [staff, g] of Object.entries(state.groups)) {
    if (g.members.includes(identity)) return { mode: g.mode, scope: staff };
  }
  return { mode: state.roomMode, scope: "room" };
};

/// "auto": everyone hears each other while waiting; once the candidate's exam runs only proctors hear them.
export const resolveMode = (mode: AudioMode, inExam: boolean): EffectiveAudio =>
  mode === "auto" ? (inExam ? "listen" : "open") : mode;

export const effectiveAudio = (state: AudioState, identity: string, inExam: boolean): EffectiveAudio =>
  resolveMode(candidateSetting(state, identity).mode, inExam);

/// The mode the current proctor controls: their own group, or the room for an admin looking at everyone.
export const myAudioMode = (state: AudioState, staffIdentity: string, wholeRoom: boolean): AudioMode =>
  wholeRoom ? state.roomMode : (state.groups[staffIdentity]?.mode ?? state.roomMode);

/// Proctors hear a candidate unless the candidate is muted, in which case only an explicit "listen" plays it.
export const staffHears = (effective: EffectiveAudio, listened: boolean) => effective !== "muted" || listened;
